/**
 * The end-of-episode card — «окно при ЗАВЕРШЕНИИ серии».
 *
 * This is where the stat system becomes visible to the player for the only time
 * in the book: five paths, each on its own ornate bar with a medallion, and the
 * running total beside it. Everything else about the stats stays hidden, which
 * is why the deferred *meter* and the stats themselves are different questions —
 * see docs/design/STAT-MODEL.md.
 *
 * The order is the Bible's own: the three paths that select an ending first,
 * then the two character stats that pick which of that family you get.
 */
import type { PackManifest } from '@hon/schema';

/** Bible name → declared variable, in the order the board lists them. */
const STATS: Array<{ name: string; label: string }> = [
  { name: 's_grehopadenie', label: 'Грехопадение' },
  { name: 's_iskuplenie', label: 'Искупление' },
  { name: 's_ugasanie', label: 'Угасание' },
  { name: 's_vernost', label: 'Верность' },
  { name: 's_samokontrol', label: 'Самоконтроль' },
];

export function EpisodeEnd({
  vars,
  pack,
  hasNext,
  onContinue,
  onLibrary,
}: {
  vars: Record<string, string | number | boolean>;
  pack: PackManifest;
  hasNext: boolean;
  onContinue: () => void;
  onLibrary: () => void;
}) {
  /*
   * All five, always.
   *
   * The panel is one painting with five slots on it — «окно при ЗАВЕРШЕНИИ
   * серии» draws Угасание between Искупление and Верность — so hiding a stat
   * the pack has not declared yet leaves a painted slot with nothing in it and
   * shifts every row below out of its place. A path the story never offered
   * reads as a zero, which is what it is.
   */
  const rows = STATS;
  void pack;

  return (
    <div className="epend" role="dialog" aria-modal="true" aria-label="Серия завершена">
      <h1 className="epend-title">СЕРИЯ ЗАВЕРШЕНА!</h1>

      {/* One painting, five slots, drawn at x12.4% y28.5% w74.9% h38.5%. */}
      <div className="epend-panel" aria-hidden="true" />

      <ul className="epend-stats">
        {rows.map((s, i) => (
          <li className={`epend-stat at-${i}`} key={s.name}>
            <span className={`epend-icon epend-icon-${i}`} aria-hidden="true" />
            <span className="epend-label">{s.label}</span>
            <span className="epend-value">{Number(vars[s.name] ?? 0)}</span>
          </li>
        ))}
      </ul>

      <button className="epend-continue" onClick={hasNext ? onContinue : onLibrary}>
        {hasNext ? 'Продолжить' : 'В библиотеку'}
      </button>
    </div>
  );
}
